
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { Action, namespace, Getter } from "vuex-class";
import Multiselect from "vue-multiselect";
import { get } from 'lodash';
import { FeaturesList, TimeZone } from "@/store/types";
import { EventDetails } from "@/store/site-monitor/types";
import ParkEventOptions, { ParkEventData } from "./site-monitor/ParkEventOptions.vue";

const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		multiselect: Multiselect,
		"park-event-options": ParkEventOptions
	}
})
export default class EventLeave extends Vue {
	$refs!: {
		leaveModal: any;
		parkNotes: HTMLInputElement;
	};

	@Getter getDefaultParkValue: number;
	@Getter getFeature: (featuresList: string[]) => boolean;
	@SiteMonitor.Getter("getEventLeaveShown") eventLeaveShown!: boolean;
	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
	@SiteMonitor.Getter
	getEventShare!: any[];
	@SiteMonitor.Getter getUserID!: number;
	@SiteMonitor.Getter getIsUploadingFile: boolean;
	@SiteMonitor.Action parkEvent: any;
	@SiteMonitor.Action eventPassControl: any;
	@SiteMonitor.Action fetchEventDetails: any;
	@SiteMonitor.Action removeAdhocUsersFromEvent: any;
	@SiteMonitor.Mutation setEventLeaveShown: any;
	@SiteMonitor.Getter("getEventLeaveNote") eventLeaveNote: string;
	@SiteMonitor.Mutation setEventLeaveNote: (value: string | null) => void;

    @Action getTimeZones: () => Promise<void>;
	@Getter("getTimeZones") timeZonesList!: TimeZone[];
	@Prop(Number) public eventid?: number;

	public otherOperator: any = null;
	public parkHours: number = 0;
	public parkMinutes: number = 10;
	public parkSeconds: number = 0;
	public parkNote: string = "";
	public parkNoteRequired: boolean = true;
	public defaultPark: number = 10;

	private isParkEventChecked: boolean = false;
	private showDateInputWarning: boolean = false;

	public get sharedOperators() {
		return this.getEventShare.filter(
			(user: any) => user.userID != null && user.userID != this.getUserID && !user.removed && user.isActive
		);
	}

	public get otherUserCount() {
		return this.sharedOperators == null ? 0 : this.sharedOperators.length;
	}

	public get canLeave() {
		if (this.otherOperator != null) return true;

		if (this.otherUserCount > 0) {
			return true;
		} else {
			if (this.parkNoteRequired && this.parkNote.length <= 0) {
				return false;
			}

			// If showing date input warning, means park until date time is invalid, so prevent leaving event
			if ((!this.isParkingAnEventOptional || this.isParkEventChecked) && this.showDateInputWarning) {
				return false;
			}

			return true;
		}
	}

	private get areaTimeZoneObject(): TimeZone | null {
		if (!this.eventDetails?.timeZoneTitle) {
			return null;
		}
		return this.timeZonesList?.find(tz => tz.id === this.eventDetails.timeZoneTitle) ?? null;
	}

	private get isParkingAnEventOptional(): boolean {
		return this.getFeature(["Alarms", "EventQueue", "OptionalParkOnLeave"]);
	}

	private get isAutoFillEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Park", "AutoFill"]);
	}

	@Watch("eventLeaveShown")
	private onEventRaiseShownChanged(value: boolean, oldValue: boolean) {
		if (value) {
			this.$refs.leaveModal.show();
			if (this.eventLeaveNote) {
				this.parkNote = this.eventLeaveNote;
			}
		} else {
			this.$refs.leaveModal.hide();
		}
	}

	@Watch("isParkEventChecked") 
	private onIsParkEventCheckedChanged(): void {
		this.showDateInputWarning = false; // Reset warning when park event checkbox is toggled
	}

	// Triggered with @shown on b-modal to focus on and highlight parked notes
	private highlightNote(): void {
		if (this.isAutoFillEnabled && this.$refs.parkNotes) {
			this.$refs.parkNotes.focus();
			this.$refs.parkNotes.select();
		}
	}

	public async created() {
		this.setEventLeaveShown(false);
		await this.fetchEventDetails(this.eventid);
		this.parkNoteRequired = this.eventDetails.parkNoteRequired;

		if (this.isAutoFillEnabled) {
			this.parkNote = this.eventDetails.parkedNote;
		}
	}

	public async mounted(){
		await this.getTimeZones();
		await this.getDefaultParkToHoursAndMinutes();
	}

	public beforeDestroy() {
		this.setEventLeaveNote(null);
	}

	public async ok() {
		if (this.canLeave) {
			if (this.otherOperator != null) {
				await this.eventPassControl({
					eventID: this.eventid,
					userID: this.otherOperator.userID
				});
			} else {
				this.removeAdhocUsersFromEvent();

				if (this.parkNote.length > 500 || (this.parkNote.length == 0 && this.parkNoteRequired)) {
					return;
				}

				if (!this.isParkEventChecked && this.isParkingAnEventOptional) {
					this.parkHours = 0;
					this.parkMinutes = 0;
					this.parkSeconds = 0;
				}

				if(!this.eventid){
					this.$notify({
					type: "error",
					title: "Park Event",
					text:
						"Unable to Park Event - there is no valid event Id"
					});
					return;
				}

				await this.parkEvent({
						eventId: this.eventid,
						hours: this.parkHours,
						minutes: this.parkMinutes,
						seconds: this.parkSeconds,
						parkNote: this.parkNote,
						parkedByMap: false
					});
			}

			this.$router.push({ path: "/event-queue" });
		}
	}

	public cancel() {
		this.isParkEventChecked = false;
		this.setEventLeaveShown(false);
	}

	private onParkOptionsChanged(data: ParkEventData): void {
		this.parkHours = data.parkHours;
		this.parkMinutes = data.parkMinutes;
	}

	private async getDefaultParkToHoursAndMinutes(): Promise<void> {
		if(this.getDefaultParkValue == 10)
		{
			this.parkHours = Math.floor(this.getDefaultParkValue / 60)
			this.parkMinutes = (this.getDefaultParkValue % 60);
			return;
		}

		this.parkHours = Math.floor(this.getDefaultParkValue / 60)
		this.parkMinutes = (this.getDefaultParkValue % 60);
	}

	private get isParkEventOptionsValid(): boolean {
		return !(this.parkHours < 0 || this.parkMinutes < 0)
	}

	private onParkDateTimeValid(isValid: boolean): void {
		this.showDateInputWarning = !isValid;
	}
}
